import { useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-heart-line',
    title: 'Expositores que aumentam as vendas de ração, acessórios e petiscos',
    description: 'Produto bem exposto é produto vendido. Gôndolas organizadas por categoria, iluminação que destaca os produtos e expositores de checkout que geram compras por impulso aumentam o ticket médio de cada visita.',
  },
  {
    icon: 'ri-drop-line',
    title: 'Materiais resistentes à umidade e fáceis de higienizar',
    description: 'MDF revestido em laminado HPL resistente a água, produtos de limpeza e desinfetantes. Superfícies lisas sem poros que não acumulam pelos e odores. Porque pet shop precisa de higiene constante.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito do pet shop completo',
    description: 'Área de vendas, balcão de atendimento, banho e tosa e recepção projetados em 3D. Você aprova o layout antes de fabricar — e garante que cada produto tenha o melhor posicionamento.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Montagem sem fechar o pet shop',
    description: 'Nossa equipe instala setor por setor, nos horários de menor movimento. Seu pet shop não perde um dia de faturamento durante a reforma.',
  },
];

const blocks = [
  {
    title: 'Gôndolas e Expositores que Organizam e Vendem Mais',
    text: [
      'O tutor que entra no pet shop para comprar ração e encontra um expositor bem montado de petiscos, brinquedos e acessórios compra muito mais do que planejava. A organização por categoria — ração, higiene, acessórios, medicamentos — facilita a navegação e aumenta o tempo de permanência na loja. E mais tempo na loja significa mais compras.',
      'Nossas gôndolas para pet shops incluem estrutura em aço com pintura eletrostática anticorrosiva, prateleiras em MDF revestido em laminado HPL branco resistente a produtos de limpeza, iluminação LED embutida que destaca os produtos, porta-preços e porta-etiquetas integrados, e expositores de checkout para petiscos e acessórios de pequeno porte que geram compras por impulso. Disponíveis em módulos adaptáveis a qualquer layout.',
    ],
    img: '/media/ia/0447.webp',
    reverse: false,
  },
  {
    title: 'Balcão de Atendimento e Área de Banho e Tosa Profissionais',
    text: [
      'O balcão de atendimento é onde o tutor deixa o pet, paga pelos serviços e compra produtos. Ele precisa transmitir organização, higiene e cuidado — os mesmos valores que o tutor espera para o animal de estimação. Um balcão bem projetado reforça a confiança e facilita a venda de serviços adicionais.',
      'Projetamos balcões de atendimento para pet shops com tampo em granito ou laminado HPL resistente a produtos de limpeza, espaço interno organizado para computador e documentos, gavetas para organizar fichas e produtos de uso frequente, e frente em MDF com o logo do pet shop. Para a área de banho e tosa, bancadas em granito ou inox resistentes à umidade, armários para produtos e equipamentos, e divisórias que criam boxes individuais para cada animal.',
    ],
    img: '/media/ia/0448.webp',
    reverse: true,
  },
  {
    title: 'Área de Exposição de Animais e Consultório Veterinário',
    text: [
      'Pet shops que vendem animais precisam de expositores especiais — limpos, bem iluminados e organizados — que mostrem os animais com dignidade e despertem o desejo de adoção. E os que oferecem consultas veterinárias precisam de um consultório que transmita competência técnica e conforto para o animal e o tutor.',
      'Para a área de exposição, projetamos vitrines e expositores com iluminação LED de alta reprodução de cor, ventilação adequada e fácil acesso para limpeza. Para o consultório veterinário, mesa de exame em inox ou granito, armários para medicamentos e equipamentos, cadeiras ergonômicas para o veterinário e o tutor, e revestimento resistente a produtos de limpeza hospitalar. Tudo projetado para o bem-estar dos animais e a confiança dos tutores.',
    ],
    img: '/media/ia/0449.webp',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: '/media/ia/0450.webp',
    alt: 'Gôndolas organizadas de pet shop com produtos expostos',
  },
  {
    src: '/media/ia/0451.webp',
    alt: 'Balcão de atendimento de pet shop',
  },
  {
    src: '/media/ia/0452.webp',
    alt: 'Área de banho e tosa com bancadas organizadas',
  },
  {
    src: '/media/ia/0453.webp',
    alt: 'Consultório veterinário profissional',
  },
];

const WA_LINK = 'https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Pet%20Shop%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!';

export default function PetShopPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Pet Shop Caruaru PE | Gôndolas e Balcões – Home Office Premium',
    description: 'Mobiliário para pet shops e clínicas veterinárias em Caruaru PE. Gôndolas, balcões, área de banho e tosa e consultório. Materiais resistentes à umidade, projeto 3D gratuito e montagem sem fechar o pet shop. Atendemos pet shops no Agreste e Sertão de Pernambuco.',
    keywords: 'mobiliário pet shop Recife, gôndola pet shop Petrolina, balcão pet shop Garanhuns, móveis veterinária Caruaru, mobiliário banho tosa Agreste PE, gôndola pet shop Pernambuco, expositor pet shop Nordeste, balcão atendimento pet shop Recife, mobiliário pet shop Caruaru',
    path: '/pet-shop',
  });

  useSchemaLD({
    id: 'schema-pet-shop',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Pet Shops e Clínicas Veterinárias | Caruaru PE',
      description: 'Gôndolas, balcões, área de banho e tosa e consultório para pet shops. Projeto 3D gratuito, equipe própria de montagem.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/pet-shop`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Pet Shops e Clínicas Veterinárias',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0454.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Pet Shops · Clínicas Veterinárias · Banho e Tosa
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Mobiliário que organiza, vende mais e transmite cuidado com os animais.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Gôndolas que aumentam o ticket médio, balcão que transmite confiança e área de banho e tosa profissional. Materiais resistentes à umidade — porque pet shop não para.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Montagem sem fechar o pet shop · Suporte local em Caruaru e Agreste
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que pet shops de Caruaru escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ambientes de Pet Shop</span> que Vendem Mais e Fidelizam Tutores
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Veja o layout completo do seu pet shop em 3D antes de investir.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Gôndolas, balcão, banho e tosa e consultório projetados em 3D. Você aprova cada detalhe antes de fabricarmos. Montagem sem fechar o pet shop.
          </p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">Ambientes de <span className="text-[#E8610A]">Pet Shop Premium</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE PERNAMBUCANO</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href={WA_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
